import type { CSSProperties } from "react";
import LandingHeader from "./LandingHeader";
import EditableText from "../ui/EditableText";

function StepArrow({
  className,
  style,
}: {
  className?: string;
  style?: CSSProperties;
}) {
  return (
    <svg
      width="70"
      height="49"
      viewBox="0 0 64 45"
      fill="currentColor"
      className={className}
      style={style}
    >
      <polygon points="53,11 42,0 0,0 0,11 11,11 23,23 11,34 0,34 0,45 42,45 53,34 64,23" />
      <polygon points="23,23 0,45 42,45 64,23" fill="black" fillOpacity="0.1" />
    </svg>
  );
}

const stepColors = ["#0D5070", "#147993", "#508999", "#9DA3A6", "#C2C8D3"];

export interface StepInput {
  title: string;
  text?: string;
  bullets?: string[];
}

interface StepsHeroSectionProps {
  heading: string;
  description?: string;
  steps?: StepInput[];
  backgroundImage?: string;
  editable?: boolean;
  onSaveHeading?: (value: string) => void;
  onSaveDescription?: (value: string) => void;
  onSaveStepTitle?: (index: number, value: string) => void;
  onSaveStepText?: (index: number, value: string) => void;
}

export default function StepsHeroSection({
  heading,
  description,
  steps = [],
  backgroundImage = "/images/instant-filing-bg.svg",
  editable = false,
  onSaveHeading,
  onSaveDescription,
  onSaveStepTitle,
  onSaveStepText,
}: StepsHeroSectionProps) {
  const preparedSteps = steps.map((step, index) => ({
    ...step,
    number: String(index + 1).padStart(2, "0"),
    color: stepColors[index % stepColors.length],
  }));

  const hasFiveSteps = steps.length === 5;

  return (
    <section
      className={`relative overflow-hidden bg-dantzBlue bg-cover bg-center px-4 pt-6 md:px-6 ${
        steps.length > 0 ? "pb-20" : "pb-12"
      }`}
      style={{ backgroundImage: `url('${backgroundImage}')` }}
    >
      <div className="absolute inset-0 bg-dantzBlue/80" />

      <LandingHeader />

      <div className="relative z-10 mx-auto mt-8 h-px w-full max-w-6xl bg-white/70" />

      <div className="relative z-10 mx-auto mt-12 max-w-3xl text-center text-white">
        <EditableText
          as="h1"
          value={heading}
          editMode={editable}
          onSave={(value) => onSaveHeading?.(value)}
          className="text-[40px] font-normal tracking-widest"
        />
        {description && (
          <EditableText
            as="p"
            value={description}
            editMode={editable}
            multiline
            onSave={(value) => onSaveDescription?.(value)}
            className="mt-4 text-sm font-light leading-relaxed text-slate-200"
          />
        )}
      </div>

      {steps.length > 0 && (
      <div
        className={`relative z-10 mx-auto mt-16 grid max-w-6xl grid-cols-1 gap-y-10 md:gap-x-[17px] ${
          steps.length === 5 ? "md:grid-cols-5" : "md:grid-cols-4"
        }`}
      >
        {preparedSteps.map((step, index) => (
          <div key={step.number} className="relative h-full">
            <div
              className="relative flex h-full flex-col rounded-md border-[3px] bg-dantzBlue p-1.5"
              style={{ borderColor: step.color }}
            >
              <div
                className="absolute -top-5 -left-3 z-20 flex h-14 w-14 items-center justify-center rounded-full text-base font-semibold text-white"
                style={{ backgroundColor: step.color }}
              >
                {step.number}
              </div>

              {index < preparedSteps.length - 1 && (
                <>
                  <StepArrow
                    className="absolute -right-[34px] top-1/2 z-[5] hidden -translate-y-1/2 md:block"
                    style={{ color: step.color }}
                  />
                  {/* Pointe seule, au-dessus du panneau blanc de la carte suivante */}
                  <StepArrow
                    className="absolute -right-[34px] top-1/2 z-20 hidden -translate-y-1/2 md:block"
                    style={{ color: step.color, clipPath: "inset(0 0 0 65px)" }}
                  />
                </>
              )}

              <div className="relative z-10 flex-1 rounded bg-white px-5 pb-5 pt-8 text-left">
                {editable ? (
                  <EditableText
                    as="h3"
                    value={step.title}
                    editMode
                    onSave={(value) => onSaveStepTitle?.(index, value)}
                    className={`font-heading uppercase font-normal tracking-wide text-dantzBlue ${
                      hasFiveSteps ? "text-[18px]" : "text-[20px]"
                    }`}
                  />
                ) : (
                  <h3
                    className={`font-heading break-words font-normal tracking-wide text-dantzBlue ${
                      hasFiveSteps ? "text-[18px]" : "text-[20px]"
                    }`}
                  >
                    {step.title.toUpperCase()}
                  </h3>
                )}
                {step.bullets ? (
                  <ul className="font-heading mt-2 list-disc space-y-1 pl-6 text-sm font-normal text-gray-600">
                    {step.bullets.map((bullet) => (
                      <li key={bullet}>{bullet}</li>
                    ))}
                  </ul>
                ) : editable ? (
                  <EditableText
                    as="p"
                    value={step.text || ""}
                    editMode
                    multiline
                    onSave={(value) => onSaveStepText?.(index, value)}
                    className="font-heading mt-2 text-sm font-normal text-gray-600"
                  />
                ) : (
                  <p className="font-heading mt-2 text-sm font-normal text-gray-600">
                    {step.text}
                  </p>
                )}
              </div>
            </div>
          </div>
        ))}
      </div>
      )}
    </section>
  );
}
